import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileText,
  Copy,
  ArrowLeft,
  Loader2,
  Users,
  Clock,
  Clapperboard,
  CheckCircle,
  AlertCircle,
  Trash2
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { useNavigate, useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScriptDetail = () => {
  const navigate = useNavigate();
  const { scriptId } = useParams();
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  useEffect(() => {
    loadScript();
  }, [scriptId]);

  const loadScript = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/scripts/${scriptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Loaded script:", response.data);
      console.log("Scenes:", response.data?.scenes?.length);
      setScript(response.data);
    } catch (error) {
      console.error("Failed to load script:", error);
      toast.error("Failed to load screenplay");
      navigate("/script-creator");
    } finally {
      setLoading(false);
    }
  };

  // Format screenplay for proper Hollywood standard when pasted into Word
  const formatScreenplayForWord = () => {
    if (!script?.scenes || script.scenes.length === 0) return '';
    
    // Standard screenplay format spacing
    const CHAR_INDENT = '                                   '; // ~37 spaces for character name (centered)
    const DIALOGUE_INDENT = '                         '; // ~25 spaces for dialogue
    const PAREN_INDENT = '                              '; // ~30 spaces for parentheticals
    
    let output = '';
    
    // Title page
    output += '\n\n\n\n\n';
    output += '                                   ' + script.title?.toUpperCase() + '\n\n';
    output += '                                   Written by\n\n';
    output += '                              AI Screenplay Adaptation\n';
    output += '\n\n\n\n\n\n\n\n\n\n';
    output += '                                                    SPEC SCRIPT\n';
    output += '\n\n\n';
    
    // Process each scene
    for (const scene of script.scenes) {
      // Scene heading - ALL CAPS, flush left (no scene numbers for spec scripts)
      output += '\n' + (scene.heading?.toUpperCase() || 'INT. LOCATION - DAY') + '\n\n';
      
      // Process scene content
      const content = scene.content || '';
      
      // Split content and format properly
      const lines = content.split('\n');
      let inDialogue = false;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) {
          output += '\n';
          inDialogue = false;
          continue;
        }
        
        // Check if this is a character name (ALL CAPS, standalone line, followed by dialogue)
        const isCharacterName = /^[A-Z][A-Z\s\.\-']+$/.test(line) && 
                                line.length < 40 &&
                                !line.startsWith('INT.') && 
                                !line.startsWith('EXT.') &&
                                !line.includes('CUT TO') &&
                                !line.includes('FADE') &&
                                !line.includes('DISSOLVE');
        
        // Check if this is a parenthetical
        const isParenthetical = /^\(.*\)$/.test(line);
        
        // Check if this is a transition
        const isTransition = /^(CUT TO:|FADE OUT\.|FADE IN:|DISSOLVE TO:|SMASH CUT:|MATCH CUT:)/.test(line.toUpperCase());
        
        if (isCharacterName) {
          output += CHAR_INDENT + line + '\n';
          inDialogue = true;
        } else if (isParenthetical && inDialogue) {
          output += PAREN_INDENT + line + '\n';
        } else if (inDialogue && !isTransition) {
          // This is dialogue
          output += DIALOGUE_INDENT + line + '\n';
        } else if (isTransition) {
          // Transitions go flush right (approximate with spaces)
          output += '                                                            ' + line + '\n\n';
          inDialogue = false;
        } else {
          // Action line - flush left, but break long paragraphs
          output += line + '\n';
          inDialogue = false;
        }
      }
      
      output += '\n';
    }
    
    return output;
  };

  // EXACT COPY from BookDetail.jsx - this works there
  const handleCopyToClipboard = async () => {
    try {
      toast.info('Preparing your screenplay content...');
      
      if (!script?.scenes || script.scenes.length === 0) {
        toast.error("No scenes to copy");
        return;
      }
      
      // Format screenplay with proper Hollywood formatting
      const screenplayText = formatScreenplayForWord();
      
      console.log('[Copy] Attempting to copy:', {
        totalScenes: script.scenes.length,
        totalLength: screenplayText.length
      });
      
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(screenplayText);
          console.log('[Copy] Successfully copied via Clipboard API');
          toast.success(`Copied screenplay (${script.scenes.length} scenes) - Paste into Word, set font to Courier 12pt!`);
          return;
        } catch (clipboardError) {
          console.warn('[Copy] Clipboard API failed, trying fallback:', clipboardError);
        }
      }
      
      // Fallback method using textarea (works in iframes)
      const textarea = document.createElement('textarea');
      textarea.value = screenplayText;
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.width = '2em';
      textarea.style.height = '2em';
      textarea.style.padding = '0';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      
      if (successful) {
        console.log('[Copy] Successfully copied via fallback method');
        toast.success(`Copied screenplay (${script.scenes.length} scenes) - Paste into Word, set font to Courier 12pt!`);
      } else {
        throw new Error('Copy command failed');
      }
    } catch (error) {
      console.error("[Copy] All copy methods failed:", error);
      toast.error("Failed to copy. Please try selecting and copying the text manually.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this screenplay?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/scripts/${scriptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Screenplay deleted");
      navigate("/script-creator");
    } catch (error) {
      toast.error("Failed to delete screenplay");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> Completed</Badge>;
      case "generating":
        return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating</Badge>;
      case "error":
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="h-3 w-3 mr-1" /> Error</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-stone-700">Loading Screenplay...</p>
        </div>
      </div>
    );
  }

  if (!script) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-medium text-stone-700">Screenplay not found</p>
          <Button onClick={() => navigate("/script-creator")} className="mt-4">
            Back to Script Creator
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100 flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 ml-0 md:ml-64 py-12 px-8">
        <div className="container mx-auto max-w-5xl">
          {/* Header */}
          <div className="mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/script-creator")}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Script Creator
            </Button>
            
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold playfair text-stone-900 flex items-center gap-3">
                  <FileText className="h-8 w-8 text-amber-600" />
                  {script.title}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-stone-600">
                  {getStatusBadge(script.status)}
                  <span className="flex items-center gap-1">
                    <Clapperboard className="h-4 w-4" />
                    {script.scene_count || script.scenes?.length || 0} Scenes
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {script.characters?.length || 0} Characters
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {script.estimated_runtime || "N/A"}
                  </span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleCopyToClipboard}
                  className="bg-green-700 hover:bg-green-600 text-white"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy to Clipboard
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleDelete}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          {script.status === "completed" ? (
            <Tabs defaultValue="scenes" className="w-full">
              <TabsList className="w-full justify-start mb-4">
                <TabsTrigger value="scenes">Scenes ({script.scenes?.length || 0})</TabsTrigger>
                <TabsTrigger value="characters">Characters ({script.characters?.length || 0})</TabsTrigger>
                <TabsTrigger value="fullscript">Full Screenplay</TabsTrigger>
              </TabsList>
              
              <TabsContent value="scenes">
                <div className="space-y-4">
                  {(script.scenes || []).map((scene, idx) => (
                    <Card key={idx} className="border-stone-200">
                      <CardHeader className="py-3 bg-amber-50">
                        <CardTitle className="text-sm font-mono text-amber-800">
                          {scene.heading}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="py-4">
                        <pre className="text-sm font-mono whitespace-pre-wrap text-stone-700">
                          {scene.content}
                        </pre>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="characters">
                <div className="grid md:grid-cols-2 gap-4">
                  {(script.characters || []).map((char, idx) => (
                    <Card key={idx} className="border-stone-200">
                      <CardContent className="py-4">
                        <p className="font-semibold text-lg">{char.name}</p>
                        {char.description && (
                          <p className="text-sm text-stone-600 mt-1">{char.description}</p>
                        )}
                        <Badge variant="outline" className="mt-2">
                          {char.dialogue_count || 0} lines
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="fullscript">
                <Card className="border-stone-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Full Screenplay</CardTitle>
                      <p className="text-sm text-stone-500 mt-1">
                        Use Ctrl+A to select all, then Ctrl+C to copy
                      </p>
                    </div>
                    <Button 
                      onClick={handleCopyToClipboard}
                      className="bg-green-700 hover:bg-green-600 text-white"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy All
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[60vh]">
                      <pre className="text-sm font-mono whitespace-pre-wrap text-stone-700 p-4">
{`${script.title?.toUpperCase()}

Written by AI Screenplay Adaptation


${(script.scenes || []).map(scene => 
`${scene.heading?.toUpperCase()}

${scene.content}

`).join('\n')}`}
                      </pre>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  {script.status === "generating" ? "Generation in Progress" : "Generation Failed"}
                </h3>
                <p className="text-stone-600">
                  {script.status === "generating" 
                    ? `Progress: ${script.progress || 0}% - ${script.progress_message || "Processing..."}`
                    : script.error_message || "An error occurred during generation."}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScriptDetail;
